import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

const CustomCursor = () => {
  const cursorX = useMotionValue(-200);
  const cursorY = useMotionValue(-200);

  // Ring follows with spring lag
  const ringX = useSpring(cursorX, { damping: 25, stiffness: 250, mass: 0.6 });
  const ringY = useSpring(cursorY, { damping: 25, stiffness: 250, mass: 0.6 });

  const [state, setState] = useState('default'); // default | hover | click | text
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Skip on touch devices
    if (window.matchMedia('(hover: none)').matches) {
      setIsMobile(true);
      return;
    }

    document.documentElement.style.cursor = 'none';

    const onMove = (e) => {
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
      setVisible(true);
    };

    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);
    const onDown = () => setState('click');
    const onUp = () => setState(s => s === 'click' ? 'default' : s);

    const onOver = (e) => {
      const el = e.target;
      const tag = el.tagName?.toLowerCase();
      const role = el.getAttribute?.('role');
      const computed = window.getComputedStyle(el).cursor;

      if (tag === 'a' || tag === 'button' || role === 'button' || computed === 'pointer') {
        setState('hover');
      } else if (tag === 'input' || tag === 'textarea') {
        setState('text');
      } else {
        setState('default');
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('mouseover', onOver);
    document.documentElement.addEventListener('mouseleave', onLeave);
    document.documentElement.addEventListener('mouseenter', onEnter);

    return () => {
      document.documentElement.style.cursor = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('mouseover', onOver);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      document.documentElement.removeEventListener('mouseenter', onEnter);
    };
  }, []);

  if (isMobile) return null;

  // State-driven values
  const configs = {
    default: {
      dot: { size: 6, bg: 'hsl(var(--foreground))', shadow: '0 0 6px hsl(var(--primary) / 0.4)' },
      ring: { size: 32, border: 'hsl(var(--primary))', bg: 'transparent', bw: '1px', shadow: '0 0 10px hsl(var(--primary) / 0.15)' },
    },
    hover: {
      dot: { size: 8, bg: 'hsl(var(--primary))', shadow: '0 0 12px hsl(var(--primary) / 0.8)' },
      ring: { size: 46, border: 'hsl(var(--primary))', bg: 'hsl(var(--primary) / 0.07)', bw: '1.5px', shadow: '0 0 20px hsl(var(--primary) / 0.35)' },
    },
    click: {
      dot: { size: 4, bg: 'hsl(var(--accent))', shadow: '0 0 10px hsl(var(--accent) / 0.9)' },
      ring: { size: 20, border: 'hsl(var(--accent))', bg: 'hsl(var(--accent) / 0.1)', bw: '1.5px', shadow: '0 0 14px hsl(var(--accent) / 0.4)' },
    },
    text: {
      dot: { size: 3, bg: 'hsl(var(--primary))', shadow: '0 0 6px hsl(var(--primary) / 0.5)' },
      ring: { size: 4, border: 'transparent', bg: 'hsl(var(--primary))', bw: '0px', shadow: 'none' },
    },
  };

  const c = configs[state] || configs.default;

  return (
    <div className="fixed inset-0 pointer-events-none z-[99999]">
      {/* Dot — exact position */}
      <motion.div
        animate={{
          width: c.dot.size,
          height: c.dot.size,
          opacity: visible ? 1 : 0,
          scale: state === 'click' ? 0.7 : 1,
        }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
        style={{
          position: 'fixed',
          x: cursorX,
          y: cursorY,
          translateX: '-50%',
          translateY: '-50%',
          borderRadius: '50%',
          background: c.dot.bg,
          boxShadow: c.dot.shadow,
          pointerEvents: 'none',
        }}
      />

      {/* Ring — spring-lagged */}
      <motion.div
        animate={{
          width: c.ring.size,
          height: c.ring.size,
          opacity: visible ? 1 : 0,
          borderColor: c.ring.border,
          backgroundColor: c.ring.bg,
          borderWidth: c.ring.bw,
        }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        style={{
          position: 'fixed',
          x: ringX,
          y: ringY,
          translateX: '-50%',
          translateY: '-50%',
          borderRadius: '50%',
          borderStyle: 'solid',
          boxShadow: c.ring.shadow,
          pointerEvents: 'none',
        }}
      />

      {/* Glow trail blob — very subtle */}
      <motion.div
        style={{
          position: 'fixed',
          x: ringX,
          y: ringY,
          translateX: '-50%',
          translateY: '-50%',
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
          opacity: visible ? 1 : 0,
        }}
      />
    </div>
  );
};

export default CustomCursor;
